/**
 * Choix de cookies de l'animateur (bandeau « Vos choix de cookies », lot 6 du chantier consentements).
 *
 * Deux finalités, chacune à accepter séparément :
 * - `product` : amélioration du produit = enregistrement des sessions animateur (PostHog, UE) ;
 * - `ads`     : publicité = pixel Meta.
 * Firebase Analytics (GA4) reste hors bandeau pour l'instant (décision D4 du 21/09/2026 : 2e temps,
 * après avoir mesuré le taux de refus réel).
 *
 * Stockage : cookie `ub_consent` posé sur `.unboared.com` (partagé entre le site et la console),
 * 6 mois. Format : `v1.a{0|1}.p{0|1}.{horodatage en s}`. Absent ou illisible = pas encore de choix.
 * ⚠️ Même format lu par `index.html` (`__loadMetaPixel`) et par le site (unboared-landing) :
 * le changer ici impose de les changer aussi.
 */

export type ConsentChoice = { ads: boolean; product: boolean; at: number };

export const CONSENT_COOKIE = 'ub_consent';
export const CONSENT_MAX_AGE_S = 182 * 24 * 3600; // ~6 mois (recommandation CNIL)
export const CONSENT_CHANGE_EVENT = 'ub-consent-change';

const PATTERN = /^v1\.a([01])\.p([01])\.(\d{9,11})$/;

export function parseConsent(raw: string | null | undefined): ConsentChoice | null {
    const m = PATTERN.exec((raw || '').trim());
    if (!m) return null;
    const at = Number(m[3]);
    // Choix périmé (> 6 mois) : on redemande, comme si rien n'avait été choisi.
    if (Date.now() / 1000 - at > CONSENT_MAX_AGE_S) return null;
    return { ads: m[1] === '1', product: m[2] === '1', at };
}

export function serializeConsent(choice: ConsentChoice): string {
    return `v1.a${choice.ads ? 1 : 0}.p${choice.product ? 1 : 0}.${Math.floor(choice.at)}`;
}

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const prefix = `${name}=`;
    const hit = document.cookie.split('; ').find((c) => c.startsWith(prefix));
    return hit ? decodeURIComponent(hit.slice(prefix.length)) : null;
}

/** `.unboared.com` en prod (partage avec le site) ; cookie de l'hôte seul ailleurs (localhost, previews). */
export function cookieDomainFor(hostname: string): string | null {
    return hostname === 'unboared.com' || hostname.endsWith('.unboared.com') ? '.unboared.com' : null;
}

export function readConsent(): ConsentChoice | null {
    return parseConsent(readCookie(CONSENT_COOKIE));
}

export function writeConsent(choice: { ads: boolean; product: boolean }): ConsentChoice {
    const full: ConsentChoice = { ...choice, at: Math.floor(Date.now() / 1000) };
    const domain = cookieDomainFor(window.location.hostname);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
        `${CONSENT_COOKIE}=${encodeURIComponent(serializeConsent(full))}; Max-Age=${CONSENT_MAX_AGE_S}; Path=/` +
        `${domain ? `; Domain=${domain}` : ''}; SameSite=Lax${secure}`;
    window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGE_EVENT, { detail: full }));
    return full;
}

/** Appelle `cb` à chaque nouveau choix (bandeau, Mon compte). Retourne le désabonnement. */
export function onConsentChange(cb: (choice: ConsentChoice) => void): () => void {
    const handler = (e: Event) => cb((e as CustomEvent<ConsentChoice>).detail);
    window.addEventListener(CONSENT_CHANGE_EVENT, handler);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}

/** « Modifier mes choix » (Mon compte) : rouvre le bandeau en mode personnalisation (ConsentLayer). */
export const CONSENT_OPEN_EVENT = 'ub-consent-open';
export function openConsentSettings(): void {
    window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}
